import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getMyUsageStats } from '../services/ai-chat-management.service';
import { AUTH_ROLES } from '../types/auth.type';

/**
 * Hook to check if current user has AI Chat access
 * SUPER users always have access and see the AI chat button on all pages
 * @returns {Object} { hasAccess, loading, usage, error }
 */
export const useAiChatAccess = () => {
    const { auth } = useSelector(({ auth }) => ({ auth }));
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [usage, setUsage] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkAccess = async () => {            
            if (!auth?._id) {
                setHasAccess(false);
                setLoading(false);
                return;
            }

            // SUPER users always have access - show AI chat button on all pages
            if (auth?.role?.slug === AUTH_ROLES.SUPER) {
                setHasAccess(true);
                setLoading(false);
                
                // Optionally fetch usage stats for SUPER users (non-blocking)
                // Don't pass facilityId - only fetch user usage stats (no facility limit)
                try {
                    const response = await getMyUsageStats(null);
                    if (response?.success && response?.data) {
                        setUsage(response.data);
                    }
                } catch (err) {
                    // Ignore errors for usage stats - SUPER user still has access
                    console.log('[AI Chat Access] Could not fetch usage stats for super user:', err);
                }
                return;
            }

            try {
                setLoading(true);
                // Note: Don't pass facilityId - only fetch user usage stats (no facility limit)
                const response = await getMyUsageStats(null);
                
                if (response?.success && response?.data) {
                    setHasAccess(true);
                    setUsage(response.data);
                } else {
                    // If response is successful but no data, still grant access (usage stats might be creating)
                    setHasAccess(true);
                    setUsage(null);
                }
            } catch (err) {
                // 403 means access denied - user doesn't have AI Chat access
                if (err?.response?.status === 403) {
                    setHasAccess(false);
                    console.log('[AI Chat Access] Access denied (403) for user');
                } else if (err?.response?.status === 401) {
                    // 401 means not authenticated
                    setHasAccess(false);
                    console.log('[AI Chat Access] Not authenticated (401)');
                } else if (err?.response?.status === 404) {
                    // 404 means usage stats not found - but user has access, so grant access
                    // Usage stats will be created on first use
                    setHasAccess(true);
                    setUsage(null);
                    console.log('[AI Chat Access] Usage stats not found (404) - granting access anyway');
                } else {
                    console.error('[AI Chat Access] Error checking access:', err);
                    setError(err.message);
                    // On other errors, assume no access for safety
                    setHasAccess(false);
                }
            } finally {
                setLoading(false);
            }
        };

        checkAccess();
    }, [auth?._id, auth?.role?.slug]);

    return { hasAccess, loading, usage, error };
};

